import axios from 'axios';

/**
 * Generates a tagline using OpenAI based on user information
 * 
 * @param {Object} userInfo - Information about the user
 * @param {string} userInfo.industry - User's industry
 * @param {string[]} [userInfo.keywords] - Keywords to emphasize in the tagline (optional)
 * @param {boolean} [userInfo.isRegeneration] - Whether this is a regeneration request (optional)
 * @param {number} [userInfo.regenerationCount] - Number of previous regenerations (optional)
 * @returns {Promise<{success: boolean, tagline?: string, error?: string}>}
 */
export const generateTagline = async (userInfo) => {
  try {
    if (!userInfo.industry) {
      return {
        success: false,
        error: 'Industry is required'
      };
    }

    
    
    // Make request to our PHP endpoint
    const response = await axios.post('/generate-tagline.php', {
      industry: userInfo.industry,
      keywords: userInfo.keywords || [],
      isRegeneration: userInfo.isRegeneration || false,
      regenerationCount: userInfo.regenerationCount || 0
    });
    
    
    
    if (response.data.success && response.data.tagline) {
      return {
        success: true,
        tagline: response.data.tagline
      };
    } else {
      throw new Error(response.data.error || 'Failed to generate tagline');
    }
  } catch (error) {
    
    
    return {
      success: false,
      error: error.message || 'Failed to generate tagline'
    };
  }
};

/**
 * Regenerates a new tagline that's different from previous attempts
 * 
 * @param {Object} userInfo - Information about the user
 * @param {string} userInfo.industry - User's industry
 * @param {string[]} userInfo.keywords - Keywords to emphasize
 * @param {number} regenerationCount - Number of previous regenerations
 * @returns {Promise<{success: boolean, tagline?: string, error?: string}>}
 */
export const regenerateTagline = async (userInfo, regenerationCount = 0) => {
  try {
    return await generateTagline({
      ...userInfo,
      isRegeneration: true,
      regenerationCount: regenerationCount
    });
  } catch (error) {
    
    return {
      success: false,
      error: error.message || 'Failed to regenerate tagline'
    };
  }
};

/**
 * Extract keywords from user input text
 * 
 * @param {string} text - Text to extract keywords from
 * @returns {string[]} Array of extracted keywords
 */
export const extractKeywords = (text) => {
  if (!text) return [];
  
  // Simple extraction based on common business terms
  // In a real application, you might use NLP for this
  const businessTerms = [
    'innovation', 'leadership', 'growth', 'success', 'excellence',
    'strategy', 'digital', 'transformation', 'solution', 'performance',
    'quality', 'customer', 'service', 'value', 'expertise',
    'efficiency', 'technology', 'future', 'vision', 'passion',
    'dedication', 'professional', 'commitment', 'integrity', 'trust',
    'partnership', 'collaboration', 'team', 'goal', 'result'
  ];
  
  // Convert text to lowercase for case-insensitive matching
  const lowercaseText = text.toLowerCase();
  
  // Find all matches
  return businessTerms.filter(term => 
    lowercaseText.includes(term.toLowerCase())
  );
};

/**
 * Enhance a tagline with asterisks around keywords to highlight them
 * 
 * @param {string} tagline - Original tagline
 * @param {string[]} keywords - Keywords to highlight
 * @returns {string} Enhanced tagline with keywords highlighted
 */
export const enhanceTagline = (tagline, keywords = []) => {
  if (!tagline || !keywords.length) return tagline;
  
  let enhancedTagline = tagline;
  
  // Sort keywords by length (descending) to handle overlapping keywords
  const sortedKeywords = [...keywords].sort((a, b) => b.length - a.length);
  
  for (const keyword of sortedKeywords) {
    if (!keyword || keyword.trim().length === 0) continue;
    
    // Create regex that matches whole words only
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
    enhancedTagline = enhancedTagline.replace(regex, `*${keyword}*`);
  }
  
  return enhancedTagline;
};

/**
 * Generate multiple tagline options for user selection
 * 
 * @param {Object} userInfo - Information about the user
 * @param {number} count - Number of options to generate (1-3)
 * @returns {Promise<{success: boolean, taglines?: string[], error?: string}>}
 */
export const generateTaglineOptions = async (userInfo, count = 3) => {
  try {
    // Limit count to reasonable range
    const optionsCount = Math.min(Math.max(count, 1), 3);
    const taglinePromises = [];
    
    // Generate multiple taglines in parallel
    for (let i = 0; i < optionsCount; i++) {
      taglinePromises.push(generateTagline(userInfo));
    }
    
    const results = await Promise.all(taglinePromises);
    const taglines = results
      .filter(result => result.success && result.tagline)
      .map(result => result.tagline);
    
    if (taglines.length === 0) {
      throw new Error('Failed to generate any valid taglines');
    }
    
    return {
      success: true,
      taglines
    };
  } catch (error) {
    
    
    return {
      success: false,
      error: error.message || 'Failed to generate tagline options',
    };
  }
};